import { RefObject, useEffect, useRef } from 'react';

/**
 * Хук для автоматической регистрации ref элемента, на котором интерфейс всегда должен быть виден.
 * Используется внутри компонентов, которые должны оставаться видимыми (например, footer, popover).
 * @param elementRef Ref элемента, который должен быть всегда виден
 * @param registerAlwaysVisibleElement Функция регистрации из useShowOnMovingPointer
 * @param condition Условие, при котором элемент должен быть зарегистрирован (например, isOpen для popover)
 */
export const useRegisterAlwaysVisibleRef = (
    elementRef: RefObject<HTMLElement | null>,
    registerAlwaysVisibleElement?: (ref: RefObject<HTMLElement | null>) => () => void,
    condition: boolean = true,
) => {
    const cleanupRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        // Cleanup предыдущей регистрации
        if (cleanupRef.current) {
            cleanupRef.current();
            cleanupRef.current = null;
        }

        // Регистрируем ref всегда, если условие выполнено
        // Не проверяем elementRef.current, т.к. элемент может появиться в DOM с задержкой (например, в портале)
        // Проверка наличия элемента происходит в useShowOnMovingPointer при проверке мыши
        if (condition && registerAlwaysVisibleElement) {
            cleanupRef.current = registerAlwaysVisibleElement(elementRef);
        }

        return () => {
            if (cleanupRef.current) {
                cleanupRef.current();
                cleanupRef.current = null;
            }
        };
    }, [elementRef, registerAlwaysVisibleElement, condition]);
};
